import {
  REG_EXP_START_WITH_CALC_NUT,
  REG_EXP_START_WITH_ENTRY_NUT
} from '@admincommon/constants/equation-nutrient.constants';
import * as CodeMirror from 'codemirror';

export class EquationEditorHelper {

  /**
   * Instance of the Codemirror editor
   */
  private readonly editor = undefined;

  /**
   * REGEX used to apply some formatting rules to the code
   */
  // numbers
  private readonly numbersRegex = /^\d+(.\d+)?/;

  // atoms
  private readonly atomsRegex = /^(TRUE|FALSE|NULL)/;

  // operators
  private readonly operatorsRegex = /^([-+/*=<>!^%]|&&|\|\|)/;

  // blocks
  private readonly blocksRegex = /^(IF\()/;

  // functions
  private readonly functionsRegex = /^[A-Z0-9]+\(/;

  // constants
  private readonly constantsRegex = /^(e|PI)/;

  // variables
  private readonly variableRegex = /^\[.*?\]/;

  // entry nutrients
  private readonly entryNutrientRegex = REG_EXP_START_WITH_ENTRY_NUT;

  // calculated nutrients
  private readonly calculatedNutrientRegex = REG_EXP_START_WITH_CALC_NUT;

  constructor(editor) {
    this.editor = editor;
  }

  /**
   * Register a listener to indent wrapped lines
   */
  registerWrappedLineIndenter() {
    // indent wrapped lines
    const charWidth = this.editor.defaultCharWidth(), basePadding = 4;
    this.editor.on('renderLine', function (cm, line, elt) {
      const off = (CodeMirror.countColumn(line.text, null, cm.getOption('tabSize')) + cm.getOption('indentUnit')) * charWidth;
      elt.style.textIndent = '-' + off + 'px';
      elt.style.paddingLeft = (basePadding + off) + 'px';
    });
    this.editor.refresh();
  }

  /**
   * Register a listener to move the cursor to the first parameter of the inserted function after autocompletion
   */
  registerAutocompletionListener() {
    this.editor.on('endCompletion', () => {
      const cursor = this.editor.getCursor();
      const line = this.editor.getLine(cursor.line);
      let end = cursor.ch;
      while (end > 0 && line.charAt(end - 1).match([/[(,]/])) {
        end--;
      }
      this.editor.setCursor(cursor.line, end);
    });
  }

  /**
   * Format and indent the editor content
   */
  formatAndIndent() {
    this.editor.setValue(this.format(this.editor.getValue()));
    this.indent();
  }

  /**
   * Reindent the editor's content
   */
  indent() {
    for (let i = 0; i < this.editor.lineCount(); i++) {
      this.editor.indentLine(i);
    }
  }

  /**
   * Insert text at the cursor position
   */
  insertTextAtCursorPos(text, moveCursorRight) {
    const cursor = this.editor.getCursor();
    // insert text at the cursor position
    this.editor.replaceRange(text, { line: cursor.line, ch: cursor.ch }, { line: cursor.line, ch: cursor.ch });
    // move the cursor 'moveCursorRight' to the right
    this.editor.setCursor(cursor.line, cursor.ch + moveCursorRight);
    this.editor.focus();
  }

  /**
   * Return completion candidates
   * (used by CodeMirror hint/show-hint.js addon)
   */
  equationHint(cm, _) {
    const completionWords = [
      'IF(,,)', 'NOT()', 'ISNULL()', 'EXP(,)', 'LOG()', 'LOG10()', 'SQRT(,)',
      'SUM(,)', 'PRODUCT(,)', 'MIN(,)', 'MAX(,)', 'AVERAGE(,)', 'MEDIAN(,)',
      'ROUND()', 'ABS()', 'FLOOR()', 'CEILING()', 'FACT()', 'SIN()', 'ASIN()',
      'COS()', 'ACOS()', 'TAN()', 'ATAN()'
    ];

    const cursor = cm.getCursor();
    const line = cm.getLine(cursor.line);
    let start = cursor.ch;
    let end = cursor.ch;

    while (start > 0 && /\w/.test(line.charAt(start - 1))) {
      start--;
    }

    while (end < line.length && /\w/.test(line.charAt(end))) {
      end++;
    }

    const word = line.slice(start, end).toLowerCase();

    return {
      list: completionWords.filter(e => e.toLowerCase().startsWith(word)),
      from: CodeMirror.Pos(cursor.line, start),
      to: CodeMirror.Pos(cursor.line, end)
    };
  }

  caseCloseParenthesis(stack, result){
    if (stack[0] === 'block') {
      if (result.endsWith('\n')) {
        result += ')\n';
      } else {
        result += '\n)\n';
      }
    } else {
      result += ')';
    }
    return result;
  }

  caseComma(stack, result){
    if (stack[0] === 'block') {
      if (result.endsWith('\n')) {
        result = result.slice(0, result.length - 1);
      }
      result += ',\n';
    } else {
      result += ', ';
    }
    return result;
  }
  /**
   * Format the equation (no indentation)
   */
  format(code) {
    const stack = [];
    let result = '';
    let previous;

    while (code.length > 0) {
      if (code === previous) {
        return result + code;
      }

      previous = code;
      code = code.trim();

      // parenthesis and comma management
      const peek = code.charAt(0);
      switch (peek) {
        case '(':
          result += '(';
          code = code.slice(1);
          stack.unshift('parenthesis');
          break;
        case ')':
          result = this.caseCloseParenthesis(stack,result);
          code = code.slice(1);
          stack.shift();
          break;
        case ',':
          result = this.caseComma(stack, result);
          code = code.slice(1);
          break;
      }

      let resultMatch = this.checkMatchs(code, stack);
      if (resultMatch != null) {
        result += resultMatch.result;
        code = resultMatch.code;
      }
    }

    return result;
  }

  /**
   * Check all matchs
   * @param code the code
   * @param stack the stack
   * @returns if match --> result = the result; code = the new code; else --> null
   */
  checkMatchs(code, stack) {
    // numbers
    let match = code.match(this.numbersRegex);
    if (match) {
      return {
        result: match[0],
        code: code.slice(match[0].length)
      }
    }

    // atoms
    match = code.match(this.atomsRegex);
    if (match) {
      return {
        result: match[0],
        code: code.slice(match[0].length)
      }
    }

    // operators
    match = code.match(this.operatorsRegex);
    if (match) {
      return {
        result: ' ' + match[0] + ' ',
        code: code.slice(match[0].length)
      }
    }

    // blocks
    match = code.match(this.blocksRegex);
    if (match) {
      stack.unshift('block');
      return {
        result: match[0] + '\n',
        code: code.slice(match[0].length)
      }
    }

    // functions
    match = code.match(this.functionsRegex);
    if (match) {
      stack.unshift('function');
      return {
        result: match[0],
        code: code.slice(match[0].length)
      }
    }

    // constants
    match = code.match(this.constantsRegex);
    if (match) {
      return {
        result: match[0],
        code: code.slice(match[0].length)
      }
    }

    // variables
    match = code.match(this.variableRegex);
    if (match) {
      return {
        result: match[0],
        code: code.slice(match[0].length)
      }
    }

    // entry nutrients
    match = code.match(this.entryNutrientRegex);
    if (match) {
      return {
        result: match[0],
        code: code.slice(match[0].length)
      }
    }

    // calculated nutrients
    match = code.match(this.calculatedNutrientRegex);
    if (match) {
      return {
        result: match[0],
        code: code.slice(match[0].length)
      }
    }

    return null;
  }
}
